import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import apiClient from '../lib/axios';
import { getApiErrorCode, getApiErrorMessage } from '../lib/apiError';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'USER';
  twoFactorEnabled?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  applyUser: (user: User) => void;
  verifyTwoFactor: (code: string) => Promise<void>;
}

export const SESSION_EXPIRED_MESSAGE = 'Your sign-in session has expired. Please log in again.';

/**
 * The sign-in session behind a 2FA challenge is gone. Distinct from a wrong
 * code, because the only way forward is to log in again; retrying is pointless.
 */
export class TwoFactorSessionExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TwoFactorSessionExpiredError';
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedUser = localStorage.getItem('user');
        const accessToken = localStorage.getItem('accessToken');

        if (savedUser && accessToken) {
          setUser(JSON.parse(savedUser));
          const response = await apiClient.get('/auth/me');
          const userData = response.data.data || response.data;
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const data = response.data.data || response.data;

      if (data.requiresTwoFactor) {
        // The signed session is all the challenge screen needs. The user's
        // details used to be stored beside it, but nothing reads them now that
        // the server takes the account from the session, so writing personal
        // details to browser storage would be for nothing.
        sessionStorage.setItem('tempSessionId', data.tempSessionId);
        // Throw special error to signal 2FA required
        const err = new Error('2FA_REQUIRED');
        (err as any).requiresTwoFactor = true;
        throw err;
      }

      const { accessToken, refreshToken, user: userData } = data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (error: any) {
      if (error.requiresTwoFactor) throw error;
      throw new Error(getApiErrorMessage(error, 'Invalid email or password'));
    }
  }, []);

  const verifyTwoFactor = useCallback(async (code: string) => {
    const tempSessionId = sessionStorage.getItem('tempSessionId');

    if (!tempSessionId) {
      throw new TwoFactorSessionExpiredError(SESSION_EXPIRED_MESSAGE);
    }

    try {
      // No userId: the server reads the account from the signed tempSessionId.
      const response = await apiClient.post('/auth/verify-2fa', { code, tempSessionId });

      const { accessToken, refreshToken, user: userData } = response.data.data || response.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      sessionStorage.removeItem('tempSessionId');
    } catch (error) {
      // A dead sign-in session cannot be retried, so drop it and let the caller
      // send the user back to login. Without this the page keeps showing "that
      // code is wrong" for a code that is perfectly valid.
      if (getApiErrorCode(error) === 'AUTH_2FA_SESSION_EXPIRED') {
        sessionStorage.removeItem('tempSessionId');
        throw new TwoFactorSessionExpiredError(getApiErrorMessage(error, SESSION_EXPIRED_MESSAGE));
      }
      throw new Error(getApiErrorMessage(error, 'Verification failed'));
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await apiClient.post('/auth/logout', { refreshToken });
      }
    } catch {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      sessionStorage.removeItem('tempSessionId');
      // Clears the key an older build wrote, so a browser that still has one
      // from before this change does not keep it forever.
      sessionStorage.removeItem('tempUser');
      setUser(null);
    }
  }, []);

  // Rethrows on purpose. Callers act on the result: the profile page only tells
  // the user a change succeeded once the refreshed record is on screen, and
  // swallowing a failure here let it claim success while still showing the old
  // values. Anything that genuinely does not care can catch and ignore.
  const refreshUser = useCallback(async () => {
    const response = await apiClient.get('/auth/me');
    const userData = response.data.data || response.data;
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  // Replace the cached user with one the server just returned, without a second
  // round trip. Used after a save that already responds with the updated record.
  const applyUser = useCallback((userData: User) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      refreshUser,
      applyUser,
      verifyTwoFactor,
    }),
    [user, isLoading, login, logout, refreshUser, applyUser, verifyTwoFactor]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
